import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Accordion } from 'semantic-ui-react'
import concat from 'lodash/concat'
import filter from 'lodash/filter'
import uniq from 'lodash/uniq'
// Local
import {
  CONDITION,
  NOTE,
  ROLES
} from '../../../constant'
import {
  condition as conditionUtils,
  conditionGroupTitle,
  filterNlpConditions,
  filterWithStatus,
  hasConditionsOrActivities,
  orderConditionsByHccConfidenceDescending
} from '../../common/conditionUtils'
import {
  encounterConditionGroups,
  encounterConditionGroupsEnabled
} from '../../common/currentUserUtils'
import * as lockUtils from '../../common/lockUtils'
import ConditionHeader from '../common/conditionHeader'
import ConditionCreate from '../common/conditionCreate'
import CdiConditionEdit from './cdiConditionEdit'
import CdiConditionRead from './cdiConditionRead'
import If from '../../common/if'
import {
  EncounterConditionDisplay,
  EncounterDetail,
  EncounterLock
} from '../../authenticated/types'
import { RootState } from '../../../store'
// Styling
import '../detail.css'

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type Props = ReduxStateProps & {
  encounterDetail: EncounterDetail
  activeConditionId: string | null
  onConditionAction: (
    action: keyof typeof CONDITION.ACTION,
    condition: (
      EncounterConditionDisplay[] |
      EncounterConditionDisplay |
      string |
      Partial<EncounterConditionDisplay>
    )
  ) => void
  onConditionNoteAction: (
    action: any,
    condition: EncounterConditionDisplay,
    content: string,
    reasonCode: string
  ) => void
}

function CdiEncounterConditionsV1(
  props: Props
): JSX.Element {
  const {
    currentUser,
    encounterDetail,
    activeConditionId = null,
    onConditionAction,
    onConditionNoteAction
  } = props

  const [
    activeConditionsState,
    setActiveConditionsState
  ] = useState<string[]>(
    activeConditionId
      ? [ activeConditionId ]
      : []
  )

  const isActive = (
    condition: EncounterConditionDisplay
  ): boolean => (
    activeConditionsState.includes(condition.id)
  )

  const addActive = (
    condition: EncounterConditionDisplay
  ): void => {
    if (!isActive(condition)) {
      let active = activeConditionsState
      // special case where only 1 condition is active at a time
      if (active.length === 1) {
        active = [ condition.id ]
      } else {
        active.push(condition.id)
      }
      setActiveConditionsState(active)
    }
  }

  const removeActive = (
    condition: EncounterConditionDisplay
  ): void => {
    if (isActive(condition)) {
      const active = filter(
        activeConditionsState,
        (id: string) => id !== condition.id
      )
      setActiveConditionsState(active)
    }
  }

  const onClickConditionTitle = (
    condition: EncounterConditionDisplay
  ): void => {
    if (isActive(condition)) {
      removeActive(condition)
    } else {
      addActive(condition)
    }
  }

  const onHeaderAction = (
    action: keyof typeof CONDITION.ACTION,
    conditions: EncounterConditionDisplay[] | string[]
  ): void => {
    const ids = (conditions as EncounterConditionDisplay[]).map(c => c.id)
    if (action === CONDITION.ACTION.EXPAND_ALL) {
      const active = uniq(concat(ids, activeConditionsState))
      setActiveConditionsState(active)
    }
    else if (action === CONDITION.ACTION.COLLAPSE_ALL) {
      const active = filter(activeConditionsState, id => !ids.includes(id))
      setActiveConditionsState(active)
    }
  }

  const onSubmitConditionNote = (
    condition: EncounterConditionDisplay,
    noteContent: string,
    noteReason: string
  ): void => {
    onConditionNoteAction(
      NOTE.ACTION.CREATE,
      condition,
      noteContent,
      noteReason
    )
  }

  const onCreateCondition = (
    condition: (
      EncounterConditionDisplay[] |
      EncounterConditionDisplay |
      string |
      Partial<EncounterConditionDisplay>
    )
  ): void => {
    onConditionAction(
      CONDITION.ACTION.CREATE,
      condition
    )
  }

  const sortConditions = (
    conditions: EncounterConditionDisplay[]
  ): EncounterConditionDisplay[] => {
    const addressed = filter(conditions, condition => conditionUtils.isConditionAddressed(condition))
    const notAddressed = filter(conditions, condition => !conditionUtils.isConditionAddressed(condition))
    return notAddressed.concat(addressed)
  }

  const detail = encounterDetail as any
  const encounterDate = detail.encounter.start
  const { lock } = detail.encounter
  const { patient } = detail.encounter.patient
  const conditions = filterNlpConditions(
    currentUser,
    detail.conditions as EncounterConditionDisplay[] | undefined
  )
  const conditionGroups = encounterConditionGroups(currentUser)
  const groupsEnabled = encounterConditionGroupsEnabled(currentUser)
  const ignoreCodes = [ CONDITION.CODE.REMOVED, CONDITION.CODE.DISMISSED ]
  const isEditMode = lockUtils.canEdit(
    lock as EncounterLock | undefined | null,
    currentUser
  )
  const key = detail.encounter && detail.encounter.id
  const displayConditions = []

  conditionGroups.forEach(g => {
    if (groupsEnabled[g] === true) {
      displayConditions.push(
        {
          title: conditionGroupTitle(g as keyof typeof CONDITION.TYPE),
          conditions: sortConditions(
            orderConditionsByHccConfidenceDescending(
              filterWithStatus(ROLES.CDI, conditions, g, ignoreCodes)
            )
          )
        }
      )
    }
  })

  displayConditions.push(
    {
      title: 'Removed Conditions',
      conditions: orderConditionsByHccConfidenceDescending(filter(conditions, c => c.conditionCode === CONDITION.CODE.REMOVED ||
        c.conditionCode === CONDITION.CODE.DISMISSED))
    }
  )

  return (
    <div key={ key }>
      {
        displayConditions.map((display, displayIndex) => {
          const hasInsightsToShow = hasConditionsOrActivities(
            display.conditions,
            currentUser.type
          )
          return (
            <div key={ display.title }>
              <ConditionHeader
                title={ display.title }
                displayIndex={ displayIndex + 1 }
                conditions={ display.conditions }
                onAction={ onHeaderAction }
              />
              { hasInsightsToShow &&
                <Accordion fluid styled>
                  {
                    display.conditions.map((condition, index) => isEditMode ? (
                      <CdiConditionEdit
                        key={ condition.id }
                        currentUser={ currentUser }
                        condition={ condition }
                        encounterDate={ encounterDate }
                        displayIndex={ index }
                        isActive={ isActive(condition) }
                        onClickTitle={ onClickConditionTitle }
                        onSubmitNote={ onSubmitConditionNote }
                        onConditionAction={ onConditionAction }
                        hccChild={ false }
                      />
                    ) : (
                      <CdiConditionRead
                        key={ condition.id }
                        currentUser={ currentUser }
                        condition={ condition }
                        displayIndex={ index }
                        isActive={ isActive(condition) }
                        onClickTitle={ onClickConditionTitle }
                        onSubmitNote={ onSubmitConditionNote }
                        hccChild={ false }
                      />
                    ))
                  }
                </Accordion>
              }
              <If isTrue={ !hasInsightsToShow }>
                <Accordion styled fluid>
                  <Accordion.Content>
                    <span>{ `No ${display.title} to show at this time.` }</span>
                  </Accordion.Content>
                </Accordion>
              </If>
            </div>
          )
        })
      }
      <If isTrue={ isEditMode }>
        <div>
          <ConditionHeader
            title="Add Condition"
            conditions={ [] }
            displayIndex={ 0 }
          />
          <Accordion fluid styled>
            <ConditionCreate
              patient={ patient }
              onSave={ onCreateCondition }
            />
          </Accordion>
        </div>
      </If>
    </div>
  )
}

export default connect(mapStateToProps)(CdiEncounterConditionsV1)
